<template>
  <div
    id="app-notification"
    ref="notification"
  />
  <show-for-feature-flag flag="show-promotional-topbar-app">
    <promotional-topbar-notification />
  </show-for-feature-flag>
  <div class="app-wrapper">
    <top-bar
      class="app-topbar"
      :logged-user="loggedUser"
    />
    <div class="app-layout__body">
      <sidebar class="app-sidebar" />

      <div class="app-content">
        <transition name="fade">
          <main-loading v-if="isLoading" />
        </transition>
        <div class="app-container">
          <router-view v-slot="{ Component }">
            <div :class="{ hidden: isLoading }">
              <component
                :is="Component"
                @loading="handleLoading"
              />
            </div>
          </router-view>
        </div>
      </div>
    </div>
    <blocked-user-error-dialog ref="blockedUserErrorDialog" />
    <privacy-policy-dialog ref="privacyPolicyDialog" />
  </div>
</template>

<script>
import MainLoading from '@/commons/components/presentational/MainLoading.vue';
import BlockedUserErrorDialog from '@/commons/components/presentational/BlockedUserErrorDialog.vue';
import TopBar from '@/commons/components/presentational/TopBar.vue';
import Sidebar from './Sidebar.vue';
import CardBanner from '@/apps/payin/payins/components/CardBanner.vue';
import { authenticate } from '@/commons/services/auth';
import sentry from '@/commons/services/sentry';
import loggedUserResource from '@/commons/resources/loggedUser';
import { companyStatus, roles } from '@transfeeradev/api-enums';
import { APPS } from '@/commons/constants/apps';
import { useResizeObserver } from '@vueuse/core';
import PrivacyPolicyDialog from '@/commons/components/container/PrivacyPolicyDialog.vue';
import PromotionalTopbarNotification from '@/commons/components/container/PromotionalTopbarNotification.vue';
import ShowForFeatureFlag from '@/commons/components/container/ShowForFeatureFlag.vue';

export default {
  components: {
    PromotionalTopbarNotification,
    ShowForFeatureFlag,
    TopBar,
    Sidebar,
    MainLoading,
    BlockedUserErrorDialog,
    PrivacyPolicyDialog,
    CardBanner
  },
  data: () => {
    return {
      APPS,

      loggedUser: null,
      isLoading: true
    };
  },

  created() {
    authenticate(this.$router);
    sentry.setup(this.$.appContext.app);
  },
  mounted() {
    this.loggedUser = loggedUserResource.get();

    const isAdmin = this.loggedUser.profile === roles.appRoles.ADMIN.value;
    const isBlocked = this.loggedUser.company.status === companyStatus.BLOCKED;

    this.$refs.blockedUserErrorDialog.openIfHasBlock();

    if (isAdmin && !isBlocked) {
      this.$refs.privacyPolicyDialog.openIfNotAccepted();
    }

    useResizeObserver(this.$refs.notification, entries => {
      document.documentElement.style.setProperty('--notification-height', entries[0].contentRect.height + 'px');
    });
  },
  methods: {
    handleLoading(isLoading) {
      this.isLoading = isLoading;
    }
  }
};
</script>

<style>
#app {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}
</style>

<style scoped>
.app-wrapper {
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
  background-color: var(--bg-bg-background);
}

.app-topbar,
.app-sidebar {
  flex-shrink: 0;
}

.app-topbar {
  position: relative;
  z-index: 1;
  width: 100%;
}

.app-layout__body {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.app-sidebar {
  overflow: hidden auto;
}

.app-content {
  flex: 1;
  min-width: 0;
  overflow: hidden auto;
  background-color: var(--bg-bg-background-alt);
}

.app-container {
  max-width: 1234px;
  margin: 0 auto;
  padding: 0 32px;
}
</style>
